interface SEOProps {
  title?: string;
  description?: string;
  /** Path-only canonical, e.g. "/terms". Origin is added automatically. */
  path?: string;
  /**
   * When true, emits `<meta name="robots" content="noindex, nofollow">`
   * — used by the invite landing page so individual codes don't end up
   * in the Google index.
   */
  noindex?: boolean;
  /**
   * Configures Safari's Smart App Banner. Passing the App Store
   * numeric ID emits `<meta name="apple-itunes-app">`, which makes
   * iOS Safari surface an "Open / View" banner at the top of the
   * page. `appArgument` is forwarded to the app as the link payload
   * when the user taps "Open" (used to auto-fill the invite code).
   */
  appBanner?: {
    appId: string;
    appArgument?: string;
  };
}

const ORIGIN = 'https://doodi.day';
const DEFAULT_TITLE = 'Doodi · Couple balance game';
const DEFAULT_DESC = 'Once a day, a moment to know each other better.';

/**
 * Returns a fragment of `<title>` + `<meta>` + `<link>` tags. Render
 * the fragment at the top of any page component.
 *
 * React 19 hoists these tags into `<head>` automatically — no Helmet
 * provider needed. The page component owns its head, the framework
 * handles deduplication and unmounting.
 */
export function useSEO({
  title,
  description,
  path = '/',
  noindex = false,
  appBanner,
}: SEOProps) {
  const fullTitle = title ? `${title} · Doodi` : DEFAULT_TITLE;
  const desc = description ?? DEFAULT_DESC;
  const canonical = `${ORIGIN}${path}`;
  const bannerContent = appBanner
    ? `app-id=${appBanner.appId}${
        appBanner.appArgument ? `, app-argument=${appBanner.appArgument}` : ''
      }`
    : null;

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content="website" />
      {/* PNG (not SVG) so Slack/iMessage/Twitter/Open Graph clients
          actually render the preview — most social previewers won't
          rasterise SVG. 1200×630 is the canonical OG/Twitter card
          aspect, used both by the marketing pages and the per-code
          invite landing. */}
      <meta property="og:image" content={`${ORIGIN}/og-image.png`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta name="twitter:image" content={`${ORIGIN}/og-image.png`} />
      <meta property="og:locale" content="en" />
      <meta name="twitter:card" content="summary_large_image" />

      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {bannerContent && (
        <meta name="apple-itunes-app" content={bannerContent} />
      )}
    </>
  );
}
